import React from 'react';

import { Add, Close } from '@mui/icons-material';
import { Grid, IconButton } from '@mui/material';

import * as S from 'commons/components/Table/Table.styles';
import { STOEditQtyTableProps, STOProcessedItem } from 'commons/types';

import { CheckBasketStyle as SB } from '../STOCheckBasket.styles';

import { BasketInfoDataTable } from './BasketInfoDataTable.component';

const Row: React.FC<{
  data: STOProcessedItem;
  name: string;
  uom: string;
  index: number;
  quantityOrdered: number;
  totalQtyPicked: number;
  handleRemove: (deleteItem: STOProcessedItem) => void;
  handlePickActual: (index: number, editItem: STOProcessedItem) => void;
}> = ({
  data,
  name,
  uom,
  index,
  totalQtyPicked,
  quantityOrdered,
  handleRemove,
  handlePickActual,
}) => {
  const onFieldChange = (key: keyof STOProcessedItem, value: string): void => {
    const clonedData = structuredClone(data);
    if (key === 'inventory_number' || key === 'bin') {
      clonedData[key] = value;
    }
    if (key === 'quantity') {
      if (!Number.isNaN(+value)) {
        const fieldQuantity = value && +value > 0 ? +value : 0;

        // check if total qty after changing value is bigger
        const diff = fieldQuantity - clonedData.quantity;
        const totalQtyPickedResult = totalQtyPicked + diff;
        const maxAddQuantity = quantityOrdered - totalQtyPicked;

        clonedData.quantity =
          totalQtyPickedResult > quantityOrdered
            ? clonedData.quantity + maxAddQuantity
            : fieldQuantity;
      }
    }
    handlePickActual(index, clonedData);
  };
  return (
    <S.TableRow>
      <SB.RemoveBatchColumn>
        <IconButton
          onClick={() => handleRemove(data)}
          data-testid={`removeBatch-${index + 1}`}
        >
          <Close fontSize="small" />
        </IconButton>
      </SB.RemoveBatchColumn>
      <SB.EditTableColumn>{name}</SB.EditTableColumn>
      <SB.EditTableColumn>{uom}</SB.EditTableColumn>
      <SB.EditTableColumn>
        <SB.EditQty
          name="bin"
          value={data.bin}
          onChange={(event) => {
            onFieldChange('bin', event.currentTarget.value);
          }}
        />
      </SB.EditTableColumn>
      <SB.EditTableColumn>
        <SB.EditQty
          name="inventory_number"
          value={data.inventory_number}
          onChange={(event) => {
            onFieldChange('inventory_number', event.currentTarget.value);
          }}
        />
      </SB.EditTableColumn>
      <SB.EditTableColumn>
        <SB.EditQty
          name="quantity"
          value={data.quantity}
          onChange={(event) => {
            onFieldChange('quantity', event.currentTarget.value);
          }}
          inputProps={{
            sx: {
              textAlign: 'right',
            },
          }}
        />
      </SB.EditTableColumn>
    </S.TableRow>
  );
};

const EditQtyTable: React.FC<STOEditQtyTableProps> = (
  params,
): React.ReactNode => {
  const {
    rowTitles,
    data,
    name,
    uom,
    basket,
    updateBasketDetail,
    quantityOrdered,
    totalQtyPicked,
  } = params;
  const handleRemoveBatch = (deleteItem: STOProcessedItem): void => {
    const newData = data.filter((item) => item !== deleteItem);
    updateBasketDetail({
      pick_actual: newData,
    });
  };
  const handleAddBatch = (): void => {
    const newItem: STOProcessedItem = {
      ...data[data.length - 1],
      bin: '',
      inventory_number: '',
      quantity: 0,
      basket,
    };
    updateBasketDetail({
      pick_actual: [...data, newItem],
    });
  };
  const handleEditPickActual = (
    index: number,
    editItem: STOProcessedItem,
  ): void => {
    const newData = structuredClone(data);
    newData[index] = editItem;
    updateBasketDetail({
      pick_actual: newData,
    });
  };
  return (
    <>
      <SB.TableWrapper>
        <BasketInfoDataTable
          handleCheckAll={() => {}}
          checkAll={false}
          rowTitles={rowTitles}
        >
          <S.TableBody>
            {data.map((item, index) => (
              <Row
                name={name}
                uom={uom}
                data={item}
                index={index}
                handleRemove={handleRemoveBatch}
                handlePickActual={handleEditPickActual}
                quantityOrdered={quantityOrdered}
                totalQtyPicked={totalQtyPicked}
              />
            ))}
          </S.TableBody>
        </BasketInfoDataTable>
      </SB.TableWrapper>
      <Grid container style={{ margin: 8, width: 'inherit' }}>
        <Grid item xs={12} style={{ textAlign: 'right' }}>
          <SB.BlueButtonText onClick={handleAddBatch} data-testid="addBatch">
            <Add /> ADD BATCH
          </SB.BlueButtonText>
        </Grid>
      </Grid>
    </>
  );
};

export default EditQtyTable;
